import * as React from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * DashboardTooltip - a single source of truth for tooltip behavior across the
 * pet dashboard (and anywhere else we want the same feel).
 *
 * Standard props baked in:
 *   - delayDuration: 200ms
 *   - disableHoverableContent: true (prevents flicker on rapid clicks)
 *   - side: "top", align: "center", sideOffset: 6
 *   - responsive max width that never clips off-screen on mobile
 *   - pointer-events-none on the content (so the tooltip never intercepts clicks)
 *
 * Accessibility:
 *   - Radix opens the tooltip on hover AND on keyboard focus, so anyone
 *     tabbing through the page sees the same hint as a mouse user.
 *   - Radix wires `aria-describedby` from the trigger to the content and
 *     sets `role="tooltip"` automatically, so screen readers announce the
 *     label when the trigger is focused. We add `aria-live="polite"` as a
 *     belt-and-braces hint for assistive tech that doesn't follow
 *     `aria-describedby` on focus changes.
 *   - The trigger child is responsible for being focusable (Link/Button/etc.
 *     already are; for divs use tabIndex={0} + role="button"). This wrapper
 *     does not add a focus ring - keep that on the child so the ring matches
 *     the element's own shape.
 *
 * Usage:
 *   <DashboardTooltip label="Open Buddy's profile">
 *     <Link to="/edit-pet/123">...</Link>
 *   </DashboardTooltip>
 */
interface DashboardTooltipProps {
  label: React.ReactNode;
  children: React.ReactElement;
  className?: string;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
  delayDuration?: number;
  /**
   * When true, the trigger renders without a TooltipContent. Use this while
   * the label depends on async data that hasn't loaded yet - it prevents the
   * tooltip from briefly showing a placeholder/empty string and flickering
   * once the real value arrives.
   */
  disabled?: boolean;
}

export const DashboardTooltip = React.forwardRef<HTMLElement, DashboardTooltipProps>(function DashboardTooltip(
  {
    label,
    children,
    className,
    side = "top",
    align = "center",
    sideOffset = 6,
    delayDuration = 200,
    disabled = false,
  },
  ref,
) {
  // Guard against rendering tooltip content before the label data is ready.
  // An empty/whitespace-only label would otherwise pop a tiny empty bubble
  // and then re-render once data loads, causing a flicker.
  const hasLabel =
    label !== null &&
    label !== undefined &&
    !(typeof label === "string" && label.trim() === "");

  const showTooltip = !disabled && hasLabel;

  // If a parent passes a ref (e.g. via Radix `asChild`), forward it onto the
  // child element so the parent can still measure / focus it.
  const child = ref
    ? React.cloneElement(children, {
        ref: (node: HTMLElement | null) => {
          if (typeof ref === "function") ref(node);
          else if (ref) (ref as React.MutableRefObject<HTMLElement | null>).current = node;
          // Preserve any existing ref on the child element.
          const existingRef = (children as unknown as { ref?: React.Ref<HTMLElement> }).ref;
          if (typeof existingRef === "function") existingRef(node);
          else if (existingRef && typeof existingRef === "object")
            (existingRef as React.MutableRefObject<HTMLElement | null>).current = node;
        },
      })
    : children;

  // Always render the same tree shape so React's reconciler doesn't try to
  // swap a Fragment for a Tooltip wrapper when the label loads asynchronously
  // (that swap was causing "Failed to execute 'removeChild'" crashes when
  // the trigger child also lives inside another Radix portal).
  return (
    <Tooltip delayDuration={delayDuration} disableHoverableContent open={showTooltip ? undefined : false}>
      <TooltipTrigger asChild>{child}</TooltipTrigger>
      <TooltipContent
        role="tooltip"
        aria-live="polite"
        side={side}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={8}
        avoidCollisions
        className={cn(
          // Cap width so long labels wrap; on small screens fall back to
          // viewport-minus-padding so the bubble never clips off-screen.
          "max-w-[calc(100vw-1rem)] sm:max-w-xs break-words pointer-events-none",
          className,
        )}
      >
        {label}
      </TooltipContent>
    </Tooltip>
  );
});
